import React, { useContext, useEffect, useState } from 'react';
import { FlexBehavior, FlexJustify, Spinner } from 'react-magma-dom';
import { RouteComponentProps } from '@reach/router';
import { RelatedWrapperComponent } from '../../components/RelatedElement/RelatedWrapperComponent';
import { ShortRelatedItem } from '../../components/RelatedElement/ShortRelatedItem';
import { ComingSoon } from '../../components/ComingSoon';
import { AccessDenied } from '../../components/AccessDenied';
import { ContactUsError } from '../../components/ContactUsError';
import {
  ExemplarWrapper,
  StyledCenteredSpinnerWrapper,
  StyledTextType,
  StyledTextTypeLabel,
  TextTypesWrapper,
} from '../../components/Styled';
import { StyledHeading, StyledHeadingNote } from '../../components/Styled/StyledRelatedNav';
import { assesmentsApi } from '../../API/assesments';
import { getStageColors } from '../../utils';
import { getHeadingMargin } from '../../utils/assessment';
import { getStageAvailability } from '../../utils/envs';
import { COLORS, EXEMPLAR_STAGES, NOT_FOUND, SHORT_RELATED_ITEM_WIDTH } from '../../constants';
import { IAssetCategory, IAsset } from '../../constants/assets';
import AuthContext from '../../contexts/authContext';

interface IAssessment extends RouteComponentProps {
  stage: string;
}

interface ApiError {
  status: number;
}

export const Assessment = (props: IAssessment): JSX.Element => {
  const { stage, navigate } = props;

  const [items, setItems] = useState<IAssetCategory | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isComingSoon, setIsComingSoon] = useState(false);
  const [isAccessDenied, setIsAccessDenied] = useState(false);

  const { setDefaultStage } = useContext(AuthContext);

  const { main: mainColor, gradient: gradientColor } = getStageColors(stage);

  useEffect(() => {
    if (!(stage in EXEMPLAR_STAGES)) {
      navigate && navigate(`/${NOT_FOUND}`);
    }

    setDefaultStage(stage);

    if (!getStageAvailability(stage)) {
      setError(false);
      setIsComingSoon(true);
      return;
    }

    (async () => {
      setIsLoading(true);
      setIsComingSoon(false);
      setIsAccessDenied(false);
      setError(false);

      try {
        const {
          data: { data },
        } = await assesmentsApi.getAssessment(stage);
        setItems(data);
      } catch (error: unknown) {
        // Type assertion for better type handling of the error
        const apiError = error as ApiError;
        if (apiError.status === 403) {
          setIsAccessDenied(true);
        } else {
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, [stage]);

  const renderAssessmentTable = (elements: IAssetCategory[], categoryName: string): JSX.Element[] => {
    return elements.map((element: IAssetCategory, index) => {
      const { name } = element;

      return (
        <StyledTextType
          data-testid="text-type-item"
          color={COLORS.blackOlive}
          hoverColor={mainColor}
          hoverFontColor={COLORS.white}
          to={`${categoryName}/${name}`}
          key={index}
        >
          <StyledTextTypeLabel>{name}</StyledTextTypeLabel>
        </StyledTextType>
      );
    });
  };

  const renderShortRelatedItem = (assessment: IAssetCategory) => {
    if (assessment.subcategories) {
      return (
        <>
          {assessment.assets && renderAssets(assessment.assets)}
          <ExemplarWrapper margin={assessment.assets ? '15px 0px 0px 0px' : '0px'} key={'assessment-table'}>
            <TextTypesWrapper>
              {assessment.subcategories && renderAssessmentTable(assessment.subcategories, assessment.name)}
            </TextTypesWrapper>
          </ExemplarWrapper>
        </>
      );
    }

    if (assessment.assets) {
      return renderAssets(assessment.assets);
    }
  };

  const renderAssets = (assets: IAsset[]) => {
    return assets.map((asset, index) => (
      <ShortRelatedItem
        title={asset.title}
        type={asset.type}
        fileLink={asset.links.self}
        defaultColor={mainColor}
        gradient={gradientColor}
        key={index}
      />
    ));
  };

  const isEmpty = (category: IAssetCategory) => {
    return !category.subcategories && !category.assets;
  };

  if (isLoading) {
    return (
      <StyledCenteredSpinnerWrapper
        behavior={FlexBehavior.container}
        justify={FlexJustify.center}
        data-testid="spinner"
      >
        <Spinner color={mainColor} size={50} />
      </StyledCenteredSpinnerWrapper>
    );
  }

  if (error) {
    return <ContactUsError />;
  }

  if (isComingSoon) {
    return <ComingSoon stageNotReady />;
  }

  if (isAccessDenied) {
    return <AccessDenied />;
  }

  if (items) {
    return (
      <RelatedWrapperComponent width={SHORT_RELATED_ITEM_WIDTH}>
        {items.assets && renderAssets(items.assets)}
        {items.subcategories &&
          items.subcategories.map((subcategory: IAssetCategory, index: number) => {
            return (
              <React.Fragment key={index}>
                <StyledHeading size={24} margin={getHeadingMargin(index)}>
                  {subcategory.name}
                  {isEmpty(subcategory) && <StyledHeadingNote>(Coming soon!)</StyledHeadingNote>}
                </StyledHeading>
                {renderShortRelatedItem(subcategory)}
              </React.Fragment>
            );
          })}
      </RelatedWrapperComponent>
    );
  }

  return <></>;
};
